import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react';

import { apiFetch } from '../../lib/apiBase';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const CATEGORY_STYLES = {
  marriage: 'border-rose-500/30 bg-rose-600/15 text-rose-700 dark:text-rose-300',
  parenting: 'border-blue-500/30 bg-blue-600/15 text-blue-700 dark:text-blue-300',
  career: 'border-amber-500/30 bg-amber-600/15 text-amber-800 dark:text-amber-300',
  faith: 'border-purple-500/30 bg-purple-600/15 text-purple-700 dark:text-purple-300',
};

const DEFAULT_FORM = {
  title: '',
  body: '',
  category: 'parenting',
  scripture: '',
  external_link: '',
  publish_date: '',
};

const DEFAULT_VERSE_FORM = {
  theme: '',
  reference: '',
  text: '',
  translation: 'World English Bible',
};

const DEFAULT_WRITER_FORM = {
  verse_id: '',
  life_situation: '',
  tone: '',
  personal_story: '',
  category: '',
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

const TONE_SUGGESTIONS = ['Warm and encouraging', 'Direct and practical', 'Reflective and hopeful'];

const inputClassName = 'border border-gray-300 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white';
const outlineButtonClassName = 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-transparent dark:text-white dark:hover:bg-[#16161a]';

const getErrorMessage = async (response, fallback) => {
  const responseBody = await response.json().catch(() => ({}));
  return typeof responseBody?.detail === 'string' ? responseBody.detail : fallback;
};

const toDateInputValue = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toLocalDateIsoString = (dateValue) => {
  if (!dateValue) return null;
  const [year, month, day] = dateValue.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day).toISOString();
};

const toPublishDatePayload = (dateValue, originalPublishDate) => {
  if (!dateValue) return null;

  if (originalPublishDate && dateValue === toDateInputValue(originalPublishDate)) {
    return originalPublishDate;
  }

  return toLocalDateIsoString(dateValue);
};

const isFutureDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() > Date.now();
};

const GemsTab = () => {
  const [gems, setGems] = useState([]);
  const [loadedStatusFilter, setLoadedStatusFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingGem, setEditingGem] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [showVerseManager, setShowVerseManager] = useState(false);
  const [verses, setVerses] = useState([]);
  const [versesLoading, setVersesLoading] = useState(false);
  const [showVerseModal, setShowVerseModal] = useState(false);
  const [editingVerse, setEditingVerse] = useState(null);
  const [verseForm, setVerseForm] = useState(DEFAULT_VERSE_FORM);
  const [savingVerse, setSavingVerse] = useState(false);
  const [deletingVerseId, setDeletingVerseId] = useState(null);

  const [showWriter, setShowWriter] = useState(false);
  const [writerStep, setWriterStep] = useState(1);
  const [writerSearch, setWriterSearch] = useState('');
  const [writerForm, setWriterForm] = useState(DEFAULT_WRITER_FORM);
  const [writing, setWriting] = useState(false);
  const gemsRequestIdRef = useRef(0);
  const statusFilterRef = useRef('all');
  const versesRequestIdRef = useRef(0);

  const fetchGems = useCallback(async (requestedFilter = statusFilterRef.current) => {
    const requestId = ++gemsRequestIdRef.current;
    setLoading(true);
    try {
      const response = await apiFetch(`/gems?status=${encodeURIComponent(requestedFilter)}`);
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to fetch gems (${response.status})`));
      }

      const data = await response.json();
      if (requestId === gemsRequestIdRef.current) {
        setGems(Array.isArray(data) ? data : []);
        setLoadedStatusFilter(requestedFilter);
      }
    } catch (error) {
      if (requestId === gemsRequestIdRef.current) {
        setGems([]);
        setLoadedStatusFilter(requestedFilter);
        console.error('Failed to fetch gems:', error);
        toast.error(error.message || 'Failed to load gems');
      }
    } finally {
      if (requestId === gemsRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchVerses = useCallback(async () => {
    const requestId = ++versesRequestIdRef.current;
    setVersesLoading(true);
    try {
      const response = await apiFetch('/verse-library');
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to fetch verses (${response.status})`));
      }

      const data = await response.json();
      if (requestId === versesRequestIdRef.current) {
        setVerses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      if (requestId === versesRequestIdRef.current) {
        console.error('Failed to fetch verse library:', error);
        toast.error(error.message || 'Failed to load verse library');
      }
    } finally {
      if (requestId === versesRequestIdRef.current) {
        setVersesLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchGems(statusFilter);
  }, [fetchGems, statusFilter]);

  useEffect(() => {
    if (showVerseManager || showWriter) {
      fetchVerses();
    }
  }, [fetchVerses, showVerseManager, showWriter]);

  const filteredVerses = useMemo(() => {
    const query = writerSearch.trim().toLowerCase();
    if (!query) return verses;

    return verses.filter((verse) => (
      `${verse.theme || ''} ${verse.reference || ''} ${verse.translation || ''}`.toLowerCase().includes(query)
    ));
  }, [verses, writerSearch]);

  const selectedVerse = useMemo(
    () => verses.find((verse) => verse.id === writerForm.verse_id),
    [verses, writerForm.verse_id],
  );

  const visibleGems = loadedStatusFilter === statusFilter ? gems : [];

  const changeStatusFilter = (nextStatusFilter) => {
    statusFilterRef.current = nextStatusFilter;
    setStatusFilter(nextStatusFilter);
  };

  const closeModal = (open) => {
    setShowModal(open);
    if (!open) {
      setEditingGem(null);
      setFormData(DEFAULT_FORM);
    }
  };

  const openAddModal = () => {
    setEditingGem(null);
    setFormData(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEditModal = (gem) => {
    setEditingGem(gem);
    setFormData({
      title: gem.title || '',
      body: gem.body || '',
      category: gem.category || 'parenting',
      scripture: gem.scripture || '',
      external_link: gem.external_link || '',
      publish_date: toDateInputValue(gem.publish_date),
    });
    setShowModal(true);
  };

  const saveGem = async (status) => {
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = editingGem ? `/gems/${editingGem._id}` : '/gems';
      const method = editingGem ? 'PUT' : 'POST';
      const response = await apiFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
          title: formData.title.trim(),
          body: formData.body.trim(),
          category: formData.category,
          scripture: formData.scripture.trim() || null,
          external_link: formData.external_link.trim() || null,
          publish_date: toPublishDatePayload(formData.publish_date, editingGem?.publish_date),
          status,
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to save gem (${response.status})`));
      }

      const action = status === 'draft' ? 'Draft saved' : editingGem?.status === 'draft' ? 'Gem published' : editingGem ? 'Gem updated' : 'Gem created';
      toast.success(action);
      closeModal(false);
      await fetchGems();
    } catch (error) {
      console.error('Failed to save gem:', error);
      toast.error(error.message || 'Failed to save gem');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    saveGem(editingGem?.status === 'draft' ? 'draft' : 'published');
  };

  const handleDelete = async (gemId) => {
    if (!window.confirm('Delete this gem?')) return;

    setDeletingId(gemId);
    try {
      const response = await apiFetch(`/gems/${gemId}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to delete gem (${response.status})`));
      }

      toast.success('Gem deleted');
      await fetchGems();
    } catch (error) {
      console.error('Failed to delete gem:', error);
      toast.error(error.message || 'Failed to delete gem');
    } finally {
      setDeletingId(null);
    }
  };

  const closeVerseModal = (open) => {
    setShowVerseModal(open);
    if (!open) {
      setEditingVerse(null);
      setVerseForm(DEFAULT_VERSE_FORM);
    }
  };

  const openAddVerseModal = () => {
    setEditingVerse(null);
    setVerseForm(DEFAULT_VERSE_FORM);
    setShowVerseModal(true);
  };

  const openEditVerseModal = (verse) => {
    setEditingVerse(verse);
    setVerseForm({
      theme: verse.theme || '',
      reference: verse.reference || '',
      text: verse.text || '',
      translation: verse.translation || 'World English Bible',
    });
    setShowVerseModal(true);
  };

  const handleSaveVerse = async (event) => {
    event.preventDefault();
    if (!verseForm.theme.trim() || !verseForm.reference.trim() || !verseForm.text.trim() || !verseForm.translation.trim()) {
      toast.error('Theme, reference, text, and translation are required');
      return;
    }

    setSavingVerse(true);
    try {
      const endpoint = editingVerse ? `/verse-library/${editingVerse.id}` : '/verse-library';
      const response = await apiFetch(endpoint, {
        method: editingVerse ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          theme: verseForm.theme.trim(),
          reference: verseForm.reference.trim(),
          text: verseForm.text,
          translation: verseForm.translation.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to save verse (${response.status})`));
      }

      toast.success(editingVerse ? 'Verse updated' : 'Verse added');
      closeVerseModal(false);
      await fetchVerses();
    } catch (error) {
      console.error('Failed to save verse:', error);
      toast.error(error.message || 'Failed to save verse');
    } finally {
      setSavingVerse(false);
    }
  };

  const handleDeleteVerse = async (verse) => {
    if (!window.confirm(`Delete ${verse.reference} from the verse library?`)) return;

    setDeletingVerseId(verse.id);
    try {
      const response = await apiFetch(`/verse-library/${verse.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to delete verse (${response.status})`));
      }

      setWriterForm((current) => current.verse_id === verse.id ? { ...current, verse_id: '' } : current);
      toast.success('Verse deleted');
      await fetchVerses();
    } catch (error) {
      console.error('Failed to delete verse:', error);
      toast.error(error.message || 'Failed to delete verse');
    } finally {
      setDeletingVerseId(null);
    }
  };

  const openWriter = () => {
    setWriterStep(1);
    setWriterSearch('');
    setWriterForm(DEFAULT_WRITER_FORM);
    setShowWriter(true);
  };

  const closeWriter = (open) => {
    if (writing && !open) return;
    setShowWriter(open);
  };

  const goToWriterBrief = () => {
    if (!writerForm.verse_id) {
      toast.error('Choose a verse before continuing');
      return;
    }
    setWriterStep(2);
  };

  const handleWriteGem = async (event) => {
    event.preventDefault();
    if (!writerForm.life_situation.trim() || !writerForm.tone.trim() || !writerForm.category) {
      toast.error('Who this is for, tone, and category are required');
      return;
    }

    setWriting(true);
    try {
      const response = await apiFetch('/admin/gems/write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verse_id: writerForm.verse_id,
          life_situation: writerForm.life_situation.trim(),
          tone: writerForm.tone.trim(),
          personal_story: writerForm.personal_story.trim() || null,
          category: writerForm.category,
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, `Failed to write Gem (${response.status})`));
      }

      const draft = await response.json();
      setShowWriter(false);
      setWriterStep(1);
      setWriterSearch('');
      setWriterForm(DEFAULT_WRITER_FORM);
      openEditModal(draft);
      toast.success('Gems Writer draft ready to review');
      await fetchGems();
    } catch (error) {
      console.error('Failed to write Gem:', error);
      toast.error(error.message || 'Failed to write Gem');
    } finally {
      setWriting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gems Library</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage the curated insights shown in the Gems tab.
            </p>
          </div>

          <Button onClick={openAddModal} className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark">
            <Plus className="h-4 w-4" />
            Add Gem
          </Button>
        </div>

        <div className="mb-5 flex flex-wrap gap-2" aria-label="Filter Gems by status">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              type="button"
              size="sm"
              variant={statusFilter === filter.value ? 'default' : 'outline'}
              className={statusFilter === filter.value ? 'bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200' : outlineButtonClassName}
              aria-pressed={statusFilter === filter.value}
              onClick={() => changeStatusFilter(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-aqua" />
          </div>
        ) : visibleGems.length === 0 ? (
          <p className="py-12 text-center text-gray-500 dark:text-gray-400">
            No {statusFilter === 'all' ? '' : `${statusFilter} `}gems found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent dark:border-[#2a2a32]">
                  <TableHead className="text-gray-700 dark:text-gray-400">Title</TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-400">Category</TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-400">Likes</TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-400">Saved By</TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-400">Created At</TableHead>
                  <TableHead className="text-right text-gray-700 dark:text-gray-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleGems.map((gem) => (
                  <TableRow key={gem._id} className="border-gray-100 hover:bg-gray-50 dark:border-[#2a2a32] dark:hover:bg-white/[0.02]">
                    <TableCell className="max-w-[320px] text-gray-900 dark:text-white">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{gem.title}</p>
                          {gem.status === 'draft' ? (
                            <Badge className="border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-200">
                              Draft
                            </Badge>
                          ) : null}
                          {isFutureDate(gem.publish_date) ? (
                            <Badge className="border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200">
                              Scheduled
                            </Badge>
                          ) : null}
                        </div>
                        <p className="line-clamp-2 text-sm text-gray-600 dark:text-gray-400">{gem.body}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_STYLES[gem.category] || CATEGORY_STYLES.faith}>
                        {gem.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{gem.likes?.length || 0}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{gem.saved_by?.length || 0}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">
                      {gem.created_at ? new Date(gem.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
                          onClick={() => openEditModal(gem)}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                          disabled={deletingId === gem._id}
                          onClick={() => handleDelete(gem._id)}
                        >
                          {deletingId === gem._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Card className="border border-gray-200 bg-white dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 p-6 text-left"
          aria-expanded={showVerseManager}
          onClick={() => setShowVerseManager((current) => !current)}
        >
          <span>
            <span className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <BookOpen className="h-5 w-5 text-aqua" />
              Manage Verse Library
            </span>
            <span className="mt-1 block text-sm text-gray-600 dark:text-gray-400">
              Add or update the approved Scripture available to Gems Writer.
            </span>
          </span>
          {showVerseManager ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
        </button>

        {showVerseManager ? (
          <div className="border-t border-gray-200 p-6 dark:border-[#2a2a32]">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Scripture text is kept exactly as entered.
              </p>
              <Button type="button" onClick={openAddVerseModal} className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark">
                <Plus className="h-4 w-4" />
                Add Verse
              </Button>
            </div>

            {versesLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-7 w-7 animate-spin text-aqua" />
              </div>
            ) : verses.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                No verses in the library yet.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-[#2a2a32]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200 hover:bg-transparent dark:border-[#2a2a32]">
                      <TableHead className="text-gray-700 dark:text-gray-400">Theme</TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-400">Reference</TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-400">Translation</TableHead>
                      <TableHead className="text-right text-gray-700 dark:text-gray-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {verses.map((verse) => (
                      <TableRow key={verse.id} className="border-gray-100 dark:border-[#2a2a32]">
                        <TableCell className="font-medium text-gray-900 dark:text-white">{verse.theme}</TableCell>
                        <TableCell className="text-gray-700 dark:text-gray-300">{verse.reference}</TableCell>
                        <TableCell className="text-gray-600 dark:text-gray-400">{verse.translation}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
                              disabled={Boolean(deletingVerseId)}
                              onClick={() => openEditVerseModal(verse)}
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
                              disabled={Boolean(deletingVerseId)}
                              onClick={() => handleDeleteVerse(verse)}
                            >
                              {deletingVerseId === verse.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        ) : null}
      </Card>

      <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <Sparkles className="h-5 w-5 text-aqua" />
              Gems Writer
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Write from approved Scripture, then review the private draft before publishing.
            </p>
          </div>
          <Button onClick={openWriter} className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark">
            <Sparkles className="h-4 w-4" />
            Open Gems Writer
          </Button>
        </div>
      </Card>

      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          if (!open && submitting) return;
          closeModal(open);
        }}
      >
        <DialogContent className="max-w-2xl border border-gray-200 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#1e1e24] dark:text-white">
          <DialogHeader>
            <DialogTitle>{editingGem?.status === 'draft' ? 'Review Draft Gem' : editingGem ? 'Edit Gem' : 'Add Gem'}</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {editingGem?.status === 'draft'
                ? 'Refine this private draft, then save it for later or publish it.'
                : 'Add a new insight or update an existing Gem.'}
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="admin-gem-title" className="text-gray-700 dark:text-gray-200">Title</Label>
              <Input
                id="admin-gem-title"
                value={formData.title}
                onChange={(event) => setFormData((current) => ({ ...current, title: event.target.value }))}
                className={inputClassName}
                placeholder="A truth worth carrying this week"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-gem-body" className="text-gray-700 dark:text-gray-200">Body</Label>
              <Textarea
                id="admin-gem-body"
                value={formData.body}
                onChange={(event) => setFormData((current) => ({ ...current, body: event.target.value }))}
                className={`min-h-[180px] ${inputClassName}`}
                placeholder="Write the full Gem here..."
                disabled={submitting}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-gem-category" className="text-gray-700 dark:text-gray-200">Category</Label>
                <select
                  id="admin-gem-category"
                  value={formData.category}
                  onChange={(event) => setFormData((current) => ({ ...current, category: event.target.value }))}
                  className={`flex h-10 w-full rounded-md px-3 py-2 text-sm ${inputClassName}`}
                  disabled={submitting}
                >
                  <option value="parenting">Parenting</option>
                  <option value="marriage">Marriage</option>
                  <option value="career">Career</option>
                  <option value="faith">Faith</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-gem-scripture" className="text-gray-700 dark:text-gray-200">Scripture</Label>
                <Textarea
                  id="admin-gem-scripture"
                  value={formData.scripture}
                  onChange={(event) => setFormData((current) => ({ ...current, scripture: event.target.value }))}
                  className={`min-h-[96px] ${inputClassName}`}
                  placeholder="Optional reference or quote"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-gem-link" className="text-gray-700 dark:text-gray-200">External Link</Label>
              <Input
                id="admin-gem-link"
                value={formData.external_link}
                onChange={(event) => setFormData((current) => ({ ...current, external_link: event.target.value }))}
                className={inputClassName}
                placeholder="https://..."
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-gem-publish-date" className="text-gray-700 dark:text-gray-200">Schedule for later (optional)</Label>
              <Input
                id="admin-gem-publish-date"
                type="date"
                value={formData.publish_date}
                onChange={(event) => setFormData((current) => ({ ...current, publish_date: event.target.value }))}
                className={inputClassName}
                disabled={submitting}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className={outlineButtonClassName} disabled={submitting} onClick={() => closeModal(false)}>
                Cancel
              </Button>
              {editingGem?.status === 'draft' ? (
                <>
                  <Button type="button" variant="outline" className={outlineButtonClassName} disabled={submitting} onClick={() => saveGem('draft')}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save Draft
                  </Button>
                  <Button type="button" disabled={submitting} className="bg-aqua text-charcoal hover:bg-aqua-dark" onClick={() => saveGem('published')}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Publish
                  </Button>
                </>
              ) : (
                <Button type="submit" disabled={submitting} className="bg-aqua text-charcoal hover:bg-aqua-dark">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {editingGem ? 'Save Changes' : 'Add Gem'}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showVerseModal}
        onOpenChange={(open) => {
          if (!open && savingVerse) return;
          closeVerseModal(open);
        }}
      >
        <DialogContent className="max-w-2xl border border-gray-200 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#1e1e24] dark:text-white">
          <DialogHeader>
            <DialogTitle>{editingVerse ? 'Edit Verse' : 'Add Verse'}</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Scripture text will be saved exactly as entered.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSaveVerse}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-verse-theme" className="text-gray-700 dark:text-gray-200">Theme</Label>
                <Input
                  id="admin-verse-theme"
                  value={verseForm.theme}
                  onChange={(event) => setVerseForm((current) => ({ ...current, theme: event.target.value }))}
                  className={inputClassName}
                  placeholder="Patience"
                  disabled={savingVerse}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-verse-reference" className="text-gray-700 dark:text-gray-200">Reference</Label>
                <Input
                  id="admin-verse-reference"
                  value={verseForm.reference}
                  onChange={(event) => setVerseForm((current) => ({ ...current, reference: event.target.value }))}
                  className={inputClassName}
                  placeholder="James 1:19"
                  disabled={savingVerse}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-verse-text" className="text-gray-700 dark:text-gray-200">Verse Text</Label>
              <Textarea
                id="admin-verse-text"
                value={verseForm.text}
                onChange={(event) => setVerseForm((current) => ({ ...current, text: event.target.value }))}
                className={`min-h-[150px] ${inputClassName}`}
                placeholder="Enter the approved verse text exactly..."
                disabled={savingVerse}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-verse-translation" className="text-gray-700 dark:text-gray-200">Translation</Label>
              <Input
                id="admin-verse-translation"
                value={verseForm.translation}
                onChange={(event) => setVerseForm((current) => ({ ...current, translation: event.target.value }))}
                className={inputClassName}
                disabled={savingVerse}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className={outlineButtonClassName} disabled={savingVerse} onClick={() => closeVerseModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingVerse} className="bg-aqua text-charcoal hover:bg-aqua-dark">
                {savingVerse ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editingVerse ? 'Save Verse' : 'Add Verse'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showWriter} onOpenChange={closeWriter}>
        <DialogContent className="max-w-3xl border border-gray-200 bg-white text-gray-900 dark:border-[#2a2a32] dark:bg-[#1e1e24] dark:text-white">
          <DialogHeader>
            <DialogTitle>Gems Writer</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Step {writerStep} of 2 — {writerStep === 1 ? 'Choose an approved verse.' : 'Shape the human reflection.'}
            </DialogDescription>
          </DialogHeader>

          {writerStep === 1 ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[240px] flex-1 space-y-2">
                  <Label htmlFor="admin-gem-verse-search" className="text-gray-700 dark:text-gray-200">Search the verse library</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="admin-gem-verse-search"
                      value={writerSearch}
                      onChange={(event) => setWriterSearch(event.target.value)}
                      className={`pl-9 ${inputClassName}`}
                      placeholder="Search by theme or reference"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className={outlineButtonClassName}
                  onClick={() => {
                    setShowWriter(false);
                    setShowVerseManager(true);
                  }}
                >
                  <BookOpen className="h-4 w-4" />
                  Manage Verse Library
                </Button>
              </div>

              <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                {versesLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-7 w-7 animate-spin text-aqua" />
                  </div>
                ) : filteredVerses.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    {verses.length === 0 ? 'No approved verses are available yet.' : 'No verses match that search.'}
                  </div>
                ) : filteredVerses.map((verse) => {
                  const isSelected = writerForm.verse_id === verse.id;
                  return (
                    <button
                      key={verse.id}
                      type="button"
                      className={`w-full rounded-lg border p-4 text-left transition-colors ${isSelected
                        ? 'border-aqua bg-aqua/10 dark:bg-aqua/10'
                        : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50 dark:border-[#2a2a32] dark:bg-[#16161a] dark:hover:border-gray-600 dark:hover:bg-white/[0.03]'}`}
                      aria-pressed={isSelected}
                      onClick={() => setWriterForm((current) => ({ ...current, verse_id: verse.id }))}
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span>
                          <span className="block text-xs font-semibold uppercase tracking-wide text-aqua-dark dark:text-aqua">{verse.theme}</span>
                          <span className="mt-1 block font-medium text-gray-900 dark:text-white">{verse.reference}</span>
                          <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">{verse.translation}</span>
                        </span>
                        {isSelected ? <Check className="mt-1 h-5 w-5 shrink-0 text-aqua-dark dark:text-aqua" /> : null}
                      </span>
                    </button>
                  );
                })}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" className={outlineButtonClassName} onClick={() => closeWriter(false)}>Cancel</Button>
                <Button type="button" disabled={!writerForm.verse_id || versesLoading} className="bg-aqua text-charcoal hover:bg-aqua-dark" onClick={goToWriterBrief}>
                  Continue
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleWriteGem}>
              {selectedVerse ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-[#2a2a32] dark:bg-[#16161a]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Selected verse</p>
                  <p className="mt-1 font-medium text-gray-900 dark:text-white">{selectedVerse.theme} — {selectedVerse.reference}</p>
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="admin-gem-life-situation" className="text-gray-700 dark:text-gray-200">Who is this for?</Label>
                <Textarea
                  id="admin-gem-life-situation"
                  value={writerForm.life_situation}
                  onChange={(event) => setWriterForm((current) => ({ ...current, life_situation: event.target.value }))}
                  className={`min-h-[100px] ${inputClassName}`}
                  placeholder="A dad who feels stretched thin at home..."
                  maxLength={1000}
                  disabled={writing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-gem-tone" className="text-gray-700 dark:text-gray-200">What tone should it carry?</Label>
                <Input
                  id="admin-gem-tone"
                  value={writerForm.tone}
                  onChange={(event) => setWriterForm((current) => ({ ...current, tone: event.target.value }))}
                  className={inputClassName}
                  placeholder="Describe the tone in your own words"
                  maxLength={120}
                  disabled={writing}
                />
                <div className="flex flex-wrap gap-2" aria-label="Tone suggestions">
                  {TONE_SUGGESTIONS.map((tone) => (
                    <Button
                      key={tone}
                      type="button"
                      size="sm"
                      variant="outline"
                      className={outlineButtonClassName}
                      disabled={writing}
                      onClick={() => setWriterForm((current) => ({ ...current, tone }))}
                    >
                      {tone}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-gem-personal-story" className="text-gray-700 dark:text-gray-200">
                  Is there a personal story or angle to include? <span className="font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
                </Label>
                <Textarea
                  id="admin-gem-personal-story"
                  value={writerForm.personal_story}
                  onChange={(event) => setWriterForm((current) => ({ ...current, personal_story: event.target.value }))}
                  className={`min-h-[100px] ${inputClassName}`}
                  placeholder="Share only what you want reflected in the draft..."
                  maxLength={2000}
                  disabled={writing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-gem-writer-category" className="text-gray-700 dark:text-gray-200">Category</Label>
                <select
                  id="admin-gem-writer-category"
                  value={writerForm.category}
                  onChange={(event) => setWriterForm((current) => ({ ...current, category: event.target.value }))}
                  className={`flex h-10 w-full rounded-md px-3 py-2 text-sm ${inputClassName}`}
                  disabled={writing}
                  required
                >
                  <option value="" disabled>Select a category</option>
                  <option value="parenting">Parenting</option>
                  <option value="marriage">Marriage</option>
                  <option value="career">Career</option>
                  <option value="faith">Faith</option>
                </select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" className={outlineButtonClassName} disabled={writing} onClick={() => setWriterStep(1)}>
                  Back
                </Button>
                <Button type="submit" disabled={writing} className="bg-aqua text-charcoal hover:bg-aqua-dark">
                  {writing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {writing ? 'Writing your Gem...' : 'Write Draft'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GemsTab;
